import type { ZodRawShape, ZodTypeAny } from 'zod';
import { z } from 'zod';

import { StaticFieldDescriptorsMap } from '../../fields/StaticField/constants';
import type { ContestManageStore } from '../types/store';

import { getFlattenFields } from './getFlattenFields';
import { getFieldQueryByField } from './getFieldQueryByField';

export const unresolvedEntityValidationFn = () => false;
const unresolvedFieldValidationMessage = 'Wait for a field to be loaded';
const unresolvedSectionValidationMessage = 'Wait for a section to be loaded';

const getFieldValidationSchema = (
  store: ContestManageStore,
  fieldKey: string,
  optionalFieldsOverrides: string[] = []
): { field: [string, ZodTypeAny] } | { fields: [string, ZodTypeAny][] } | undefined => {
  const field = store.fields[fieldKey];
  if (!field) {
    return;
  }
  const fieldQuery = getFieldQueryByField(store, field);

  if (fieldQuery.isLoading) {
    return {
      field: [
        fieldQuery.key,
        z.custom(unresolvedEntityValidationFn, unresolvedFieldValidationMessage),
      ],
    };
  }

  if (fieldQuery.type === 'DynamicFields') {
    if (!fieldQuery.data) {
      if (field.type === 'DynamicFields' && field.validation?.isOptionalWhenDependenciesUnmet) {
        return;
      }

      return {
        field: [
          fieldQuery.key,
          z.custom(unresolvedEntityValidationFn, unresolvedFieldValidationMessage),
        ],
      };
    }

    return {
      fields: fieldQuery.data.flatMap((fKey) => {
        const result = getFieldValidationSchema(store, fKey, optionalFieldsOverrides);
        if (!result) {
          return [];
        }
        if ('field' in result) {
          const [key, schema] = result.field;
          return [[key, schema]];
        }

        return result.fields;
      }),
    };
  }

  if (!fieldQuery.data) {
    if (field.type === 'DynamicField' && field.validation?.isOptionalWhenDependenciesUnmet) {
      return;
    }

    // We dont need validation for table fields
    // As they dont have any selection logic
    if (field?.endpointName?.toLowerCase()?.includes('table')) {
      return;
    }

    return {
      field: [fieldQuery.key, z.custom(unresolvedEntityValidationFn, fieldQuery.key)],
    };
  }

  const fieldDescriptor = StaticFieldDescriptorsMap[fieldQuery.data.type];

  if (!fieldDescriptor) {
    return; // TODO: (?) No check for unsupported formats
  }

  const fieldSchemaCreator = fieldDescriptor.createValidationSchema;

  if (!fieldSchemaCreator) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  let schema = fieldSchemaCreator(fieldQuery.data, (fieldKey: string) => {
    const nestedOptionalFieldsOverrides = optionalFieldsOverrides
      .filter((key) => key.startsWith(`${fieldQuery.data.key}.`))
      .map((key) => key.replace(`${fieldQuery.data.key}.`, ''));
    return getFieldValidationSchema(store, fieldKey, nestedOptionalFieldsOverrides);
  });

  if (!schema) {
    return;
  }

  if (optionalFieldsOverrides.includes(fieldQuery.data.key)) {
    schema = schema.optional();
  }

  return {
    field: [fieldQuery.data.key, schema],
  };
};

export const getSectionValidationSchema = (
  store: ContestManageStore,
  sectionQueryKey: string,
  optionalFieldsOverrides: string[] = []
): ZodTypeAny | undefined => {
  const sectionFieldsKeys = getFlattenFields(store, sectionQueryKey);

  if (!sectionFieldsKeys.length) {
    return z.custom(unresolvedEntityValidationFn, unresolvedSectionValidationMessage);
  }

  // We dont need validation for SkippedSection
  // as we dont display anything on UI
  if (sectionFieldsKeys === 'SkippedSection') {
    return;
  }

  const schema: ZodRawShape = {};

  sectionFieldsKeys.forEach((fieldKey) => {
    const result = getFieldValidationSchema(store, fieldKey, optionalFieldsOverrides);
    if (!result) {
      return;
    }

    if ('field' in result) {
      const [key, fieldSchema] = result.field;
      schema[key] = fieldSchema;
    } else {
      result.fields.forEach(([key, fieldSchema]) => {
        schema[key] = fieldSchema;
      });
    }
  });

  if (Object.keys(schema).length === 0) {
    return;
  }
  return z.object(schema);
};
